import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { API_ENDPOINTS } from "../../constants/apiConstants";
import { getAccessToken } from "../../helpers/authHelpers";
import { iThunkApi } from "../../types/commonTypes";
import {
	iAcceptTermsResult,
	iCurrentTerms,
	iRemindResult,
	iTermsContent,
	iTermsVersion,
	TermsUnavailableReasonEnum,
} from "../../types/termsTypes";
import { apiManager } from "../apiManager";

const authHeader = () => ({ Authorization: getAccessToken() });

/**
 * Every terms endpoint answers with the standard `{ statusCode, message, data }`
 * envelope, so the payload sits one level deeper than the raw interface.
 *
 * It has to be that shape: the portal reaches Baqaal through Tabil's proxy,
 * which rebuilds each response from `response.data.statusCode / .message /
 * .data` (`Tabil/src/middlewares/request-router.js`). A bare body would arrive
 * here as `data: {}` with the payload silently dropped.
 */
const unwrap = <T>(resp: { data?: { data?: T } }): T => resp.data?.data as T;

const errorPayload = (error: unknown) => {
	const response = (error as AxiosError)?.response;
	return {
		statusCode: response?.status ?? null,
		message: (response?.data as { message?: string })?.message ?? "Something went wrong",
	};
};

/**
 * The gate check. Called on layout mount and again on window focus.
 *
 * Rejects with a `reason` on 404/503 so the slice can **fail open**: Baqaal
 * answers 404 when no version is published (the state before Legal publishes)
 * and 503 when the CMS is unreachable, and the server guard itself allows the
 * request in both cases. Treating either as "not accepted" would lock every
 * vendor out of the portal.
 */
export const fetchCurrentTermsThunk = createAsyncThunk<
	iCurrentTerms,
	void,
	iThunkApi & { rejectValue: { statusCode: number | null; message: string; reason?: TermsUnavailableReasonEnum } }
>("terms/current", async (_, thunk) => {
	try {
		const resp = await apiManager(API_ENDPOINTS.TERMS_CURRENT, {}, "GET", authHeader());
		return unwrap<iCurrentTerms>(resp);
	} catch (error) {
		const status = (error as AxiosError)?.response?.status;
		const reason =
			status === 404
				? TermsUnavailableReasonEnum.NOT_PUBLISHED
				: status === 503
				? TermsUnavailableReasonEnum.SERVICE_UNAVAILABLE
				: undefined;

		// Failing open is deliberate but not silent: with no surfaces rendered
		// there is nothing for a vendor to notice, so this is the only signal that
		// the gate is not running. 404 before Legal publishes is expected and
		// stays at info; anything else means something is actually wrong.
		Sentry.captureMessage("Supplier T&Cs gate unavailable — failing open", {
			level: reason === TermsUnavailableReasonEnum.NOT_PUBLISHED ? "info" : "warning",
			extra: { status, reason },
		});

		return thunk.rejectWithValue({ ...errorPayload(error), reason });
	}
});

/** The document body for one version. Superseded versions keep answering forever. */
export const fetchTermsContentThunk = createAsyncThunk<iTermsContent, string, iThunkApi>(
	"terms/content",
	async (version, thunk) => {
		try {
			const resp = await apiManager(API_ENDPOINTS.TERMS_CONTENT(version), {}, "GET", authHeader());
			return unwrap<iTermsContent>(resp);
		} catch (error) {
			return thunk.rejectWithValue(errorPayload(error));
		}
	},
);

/**
 * Metadata for one version, current or superseded — in particular its own
 * `effectiveDate`, which `current.effectiveDate` cannot stand in for once the
 * read-only viewer is showing a vendor's accepted (possibly superseded)
 * version rather than the live one.
 */
export const fetchTermsVersionThunk = createAsyncThunk<iTermsVersion, string, iThunkApi>(
	"terms/version",
	async (version, thunk) => {
		try {
			const resp = await apiManager(API_ENDPOINTS.TERMS_VERSION(version), {}, "GET", authHeader());
			return unwrap<iTermsVersion>(resp);
		} catch (error) {
			return thunk.rejectWithValue(errorPayload(error));
		}
	},
);

/**
 * Records acceptance. `SUPER_ADMIN` only — the server re-validates both the role
 * and the version, so a stale client cannot accept a superseded document.
 *
 * Returns only `{ version, acceptedAt }`, so the caller must refetch
 * `current` to reach `accepted`. Never synthesize that state locally.
 */
export const acceptTermsThunk = createAsyncThunk<iAcceptTermsResult, string, iThunkApi>(
	"terms/accept",
	async (version, thunk) => {
		try {
			const resp = await apiManager(API_ENDPOINTS.TERMS_ACCEPT, { version }, "POST", authHeader());
			return unwrap<iAcceptTermsResult>(resp);
		} catch (error) {
			return thunk.rejectWithValue(errorPayload(error));
		}
	},
);

/**
 * Emails every Super Admin on the vendor.
 *
 * A second call inside the 24h window is **not** an error — it returns 200 with
 * the existing `cooldownExpiresAt`, because the cooldown is per vendor and a
 * team member who taps a button someone else already tapped has done nothing
 * wrong. Drive the disabled state from that value, never from local state.
 */
export const remindSuperAdminsThunk = createAsyncThunk<iRemindResult, void, iThunkApi>(
	"terms/remind",
	async (_, thunk) => {
		try {
			const resp = await apiManager(API_ENDPOINTS.TERMS_REMIND, {}, "POST", authHeader());
			return unwrap<iRemindResult>(resp);
		} catch (error) {
			return thunk.rejectWithValue(errorPayload(error));
		}
	},
);
